import { t } from '@kanbios/ui';

/** Relative time in the DSM wording (« il y a 12 s »). */
export function relativeTime(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return t('servers.noData');
  const seconds = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (seconds < 5) return t('time.justNow');
  if (seconds < 60) return t('time.secondsAgo', { n: seconds });
  if (seconds < 3600) return t('time.minutesAgo', { n: Math.round(seconds / 60) });
  return t('time.hoursAgo', { n: Math.round(seconds / 3600) });
}

export function clockTime(iso: string | null | undefined): string {
  if (!iso) return t('servers.noData');
  return new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' }).format(new Date(iso));
}

const PARIS_PARTS = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'Europe/Paris',
});

/** The day and time of an instant in Europe/Paris, as numbers (« 16 », « 12 », « 2026 », « 14 », « 05 »). */
function parisParts(iso: string): Record<'day' | 'month' | 'year' | 'hour' | 'minute', string> | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const parts = Object.fromEntries(PARIS_PARTS.formatToParts(date).map(p => [p.type, p.value]));
  return { day: parts.day!, month: parts.month!, year: parts.year!, hour: parts.hour!, minute: parts.minute! };
}

/**
 * D58: every absolute date of the portal reads « 16/12/2026 » (Europe/Paris).
 * A plain day (« 2026-12-16 ») is that day, whatever the time zone.
 */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return t('servers.noData');
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (day) return `${day[3]}/${day[2]}/${day[1]}`;
  const p = parisParts(iso);
  return p ? `${p.day}/${p.month}/${p.year}` : iso;
}

/** D58: an absolute date with its time, « 16/12/2026 14:05 » (Europe/Paris). */
export function shortDateTime(iso: string | null | undefined): string {
  if (!iso) return t('servers.noData');
  const p = parisParts(iso);
  return p ? `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}` : iso;
}

/** Only same-site relative paths survive a sign-in round trip. */
export function safeReturnTo(value: string | null): string {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return '/servers';
  // Browsers drop tabs and newlines in URLs: `/\t/evil` would become `//evil`.
  if (/[\u0000-\u001f\u007f]/.test(value)) return '/servers';
  const resolved = new URL(value, window.location.origin);
  return resolved.origin === window.location.origin ? `${resolved.pathname}${resolved.search}${resolved.hash}` : '/servers';
}

/** How long since a moment, in the short form of the screens (« 3 min », « 8 h », « 4 j »). */
export function durationSince(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return t('servers.noData');
  const minutes = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60_000));
  if (minutes < 60) return t('time.minutes', { n: Math.max(1, minutes) });
  if (minutes < 48 * 60) return t('time.hours', { n: Math.round(minutes / 60) });
  return t('time.days', { n: Math.round(minutes / 1440) });
}

/** SPEC-034: a release named after its commit (sha-<40 hex>) said with its short commit, as Git does. */
export function shortVersion(version: string | null | undefined): string {
  if (!version) return '';
  return /^sha-[0-9a-f]{40}$/.test(version) ? version.slice(0, 11) : version;
}

/**
 * The part of the names every container shares (the stack's name, `barreau-lyon-vitrine-extranet-cms-`), cut at a
 * separator so a name never loses half a word; empty when there is a single container or nothing in common.
 */
export function sharedPrefix(names: string[]): string {
  if (names.length < 2) return '';
  let prefix = names[0] ?? '';
  for (const name of names) while (!name.startsWith(prefix)) prefix = prefix.slice(0, -1);
  const cut = Math.max(prefix.lastIndexOf('-'), prefix.lastIndexOf('_'), prefix.lastIndexOf('.'));
  prefix = cut < 0 ? '' : prefix.slice(0, cut + 1);
  // A name never becomes empty.
  return names.some(n => n.length === prefix.length) ? '' : prefix;
}
